import { useEffect, useState } from "react";
import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, Settings, FileSpreadsheet, ScrollText, LogOut, Menu, Bell, Users, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/", label: "Portafoglio", icon: LayoutDashboard, end: true },
  { to: "/operazioni", label: "Operazioni", icon: Building2 },
  { to: "/import", label: "Importa Excel", icon: FileSpreadsheet, perm: "import.run" },
  { to: "/audit", label: "Audit log", icon: ScrollText, perm: "audit.read" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, perm: "settings.read" },
];

export default function Layout() {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const loc = useLocation();
  const nav = useNavigate();
  useEffect(() => setOpen(false), [loc.pathname]);
  const { data: notifs = [] } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications").then((r) => r.data), refetchInterval: 60000 });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });
  const unread = notifs.filter((n) => !n.read).length;
  const crumbs = loc.pathname.split("/").filter(Boolean);
  const crumbLabel = (c, i) => {
    const p = projects.find((x) => x.id === c);
    if (p) return `${p.code} · ${p.name}`;
    return c.replace(/-/g, " ").replace(/^\w/, (x) => x.toUpperCase());
  };
  const results = search.length > 1 ? projects.filter((p) => `${p.code} ${p.name} ${p.city}`.toLowerCase().includes(search.toLowerCase())).slice(0, 6) : [];

  return (
    <div className="min-h-screen flex bg-background">
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#064E3B] text-white flex flex-col transition-transform duration-200 md:translate-x-0 md:static ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="sidebar">
        <div className="px-5 py-5 border-b border-white/10">
          <Link to="/" className="font-heading text-xl font-bold tracking-tight" data-testid="brand-link">Contologico</Link>
          <p className="text-xs text-emerald-200/80 mt-1">Controllo operazioni immobiliari</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.filter((n) => !n.perm || can(n.perm)).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `side-link ${isActive ? "active" : ""}`} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-white/10 text-sm">
          <div className="font-medium truncate" data-testid="user-name">{user?.name}</div>
          <div className="text-xs text-emerald-200/80 truncate">{user?.email}</div>
          <div className="text-xs mt-1 inline-block px-2 py-0.5 rounded bg-white/10" data-testid="user-role">{user?.role}</div>
          <button onClick={logout} className="mt-3 flex items-center gap-2 text-emerald-100 hover:text-white transition-colors duration-200" data-testid="logout-btn"><LogOut className="w-4 h-4" /> Esci</button>
        </div>
      </aside>
      {open && <button aria-label="Chiudi menu" className="fixed inset-0 bg-black/30 z-30 md:hidden" onClick={() => setOpen(false)} />}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b flex items-center gap-3 px-4 sticky top-0 z-20">
          <button className="md:hidden p-2" onClick={() => setOpen(true)} aria-label="Apri menu" data-testid="menu-btn"><Menu className="w-5 h-5" /></button>
          <nav className="text-sm text-slate-500 flex items-center gap-1 flex-wrap" aria-label="breadcrumb" data-testid="breadcrumb">
            <Link to="/" className="hover:text-primary">Home</Link>
            {crumbs.map((c, i) => (
              <span key={i} className="flex items-center gap-1">
                <span>/</span>
                <Link to={"/" + crumbs.slice(0, i + 1).join("/")} className="hover:text-primary">{crumbLabel(c, i)}</Link>
              </span>
            ))}
          </nav>
          <div className="ml-auto relative hidden sm:block">
            <div className="flex items-center gap-2 border rounded-md px-2 py-1 bg-slate-50">
              <Search className="w-4 h-4 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca operazione…" className="bg-transparent text-sm outline-none w-48" data-testid="global-search" />
            </div>
            {results.length > 0 && (
              <div className="absolute right-0 mt-1 w-80 bg-white border rounded-md shadow-lg z-30">
                {results.map((p) => (
                  <button key={p.id} className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50" onClick={() => { setSearch(""); nav(`/operazioni/${p.id}`); }} data-testid={`search-result-${p.code}`}>
                    <span className="font-medium">{p.code}</span> · {p.name} <span className="text-slate-400">{p.city}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Link to="/notifiche" className="relative p-2" data-testid="notifications-btn"><Bell className="w-5 h-5 text-slate-600" />{unread > 0 && <span className="absolute -top-0.5 -right-0.5 bg-red-700 text-white text-[10px] rounded-full px-1.5">{unread}</span>}</Link>
          {can("users.manage") && <Link to="/impostazioni?tab=utenti" className="p-2 hidden sm:block" data-testid="users-btn"><Users className="w-5 h-5 text-slate-600" /></Link>}
        </header>
        <main className="flex-1 p-4 md:p-8 fade-in"><Outlet /></main>
      </div>
      <Toaster richColors position="top-right" />
    </div>
  );
}
